import { z } from 'zod'

// --- Domain shapes returned by the API ---

export const VenueTagDto = z.object({
  id: z.number().int().positive(),
  groupId: z.number().int().positive(),
  label: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  colorId: z.string(),
  displayOrder: z.number(),
  venueCount: z.number().int().nonnegative(),
  venueIds: z.array(z.number().int().positive()),
  // Unique venue_group_ids covering every venue this tag is attached to.
  // Used by the browse-view filter to hide tag rows whose venues don't belong
  // to the active venue group — independent of Typesense space counts, which
  // read 0 for ghost venues / venues without indexed spaces yet.
  venueGroupIds: z.array(z.number().int().positive()),
})
export type VenueTagDto = z.infer<typeof VenueTagDto>

export const VenueTagGroupDto = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  displayOrder: z.number(),
  tags: z.array(VenueTagDto),
})
export type VenueTagGroupDto = z.infer<typeof VenueTagGroupDto>

export const TaggedVenueDto = z.object({
  id: z.number(),
  name: z.string(),
  address: z.string(),
})
export type TaggedVenueDto = z.infer<typeof TaggedVenueDto>

// --- Tag route contracts ---

export const ListVenueTagsOutput = z.object({
  groups: z.array(VenueTagGroupDto),
})

const TagWritePayload = z.object({
  label: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  colorId: z.string().min(1).max(64),
})

export const CreateTagInput = TagWritePayload.extend({
  groupId: z.number().int().positive(),
})
export const CreateTagOutput = z.object({ tag: VenueTagDto })

export const UpdateTagInput = TagWritePayload.extend({
  tagId: z.number().int().positive(),
})
export const UpdateTagOutput = z.object({ tag: VenueTagDto })

export const DeleteTagInput = z.object({ tagId: z.number().int().positive() })
export const DeleteTagOutput = z.object({ ok: z.literal(true) })

export const ReorderTagsInput = z.object({
  groupId: z.number().int().positive(),
  tagIds: z.array(z.number().int().positive()).min(1),
})
export const ReorderTagsOutput = z.object({ ok: z.literal(true) })

export const ListTaggedVenuesInput = z.object({
  tagId: z.number().int().positive(),
  search: z.string().optional(),
  limit: z.number().int().min(1).max(200).default(100),
  offset: z.number().int().min(0).default(0),
})
export const ListTaggedVenuesOutput = z.object({
  venues: z.array(TaggedVenueDto),
  total: z.number().int().nonnegative(),
})

export const AddMembershipInput = z.object({
  tagId: z.number().int().positive(),
  venueId: z.number().int().positive(),
})
export const AddMembershipOutput = z.object({ ok: z.literal(true) })

export const RemoveMembershipInput = z.object({
  tagId: z.number().int().positive(),
  venueId: z.number().int().positive(),
})
export const RemoveMembershipOutput = z.object({ ok: z.literal(true) })

// Fired by the client after any successful tag-add. Server fans the alert
// out to Slack — the client never has Slack credentials.
//
// Channel routing (server-side):
//   - Always: #new-entity-specific-tag (every successful tag → RevelNotifier).
//   - Additionally, when `venueWasExistingGhost` is true: #new-ghost-venue.
// The "new ghost venue created" notification stays on the createGhostVenue
// route's existing webhook so it isn't duplicated here.
export const NotifyTagAddedInput = z.object({
  tagId: z.number().int().positive(),
  tagName: z.string().min(1),
  venueId: z.number().int().positive(),
  venueName: z.string().min(1),
  venueWasExistingGhost: z.boolean(),
})
export const NotifyTagAddedOutput = z.object({ ok: z.literal(true) })

// --- Group route contracts ---

const GroupWritePayload = z.object({
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
})

export const CreateGroupInput = GroupWritePayload
export const CreateGroupOutput = z.object({ group: VenueTagGroupDto })

export const UpdateGroupInput = GroupWritePayload.extend({
  groupId: z.number().int().positive(),
})
export const UpdateGroupOutput = z.object({ group: VenueTagGroupDto })

export const DeleteGroupInput = z.object({ groupId: z.number().int().positive() })
export const DeleteGroupOutput = z.object({ ok: z.literal(true) })

export const ReorderGroupsInput = z.object({
  groupIds: z.array(z.number().int().positive()).min(1),
})
export const ReorderGroupsOutput = z.object({ ok: z.literal(true) })
